import React, { useState, useEffect, useRef } from "react";
import { Formik, Field, Form } from "formik";
import {
  InputGroup,
  FieldWrappers,
  Label,
  SubmitBtn,
  SideCancelBtn,
  Wrapper,
  ScrollableContent,
  ActionSection,
  CombineInputGroup,
  RupeeIcon,
  ModalSubHeading,
  ModalHeading,
  PercentIcon,
} from "../Styles/InputStyles";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import * as Yup from "yup";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import "react-datepicker/dist/react-datepicker.css";
import { useSelector } from "react-redux";
import {
  getErrorBorder,
} from "../../helpers";
import axios from "axios";
import Config from "../../Config";
import { useQuery } from "react-query";
import CategoryCard from "../CategoryCard";
import SelectItemModal from "../SelectItemModal";

// Step 1 Validation Schema (Dynamic based on property type)
const getStepOneSchema = () => {
  let schema = {
    // ownerName: Yup.string()
    //   .matches(/^[A-Za-z ]+$/, "Only alphabets and spaces are allowed")
    //   .min(3, "Landlord name must be at least 3 characters long")
    //   .required("Owner name is required"),
    rentPayable: Yup.number()
      .typeError("Rent Payable must be a number")
      .min(1000, "Rent deposit cannot be less than 1000")
      .max(2000000, "Rent deposit cannot exceed ₹20,00,000")
      .required("Rent Payable is required"),
    securityDeposit: Yup.number()
      .min(0, "Security deposit cannot be negative")
      .max(5000000, "Security deposit cannot exceed ₹50,00,000")
      .required("Security deposit is required"),
    noticePeriod: Yup.number()
      .min(1, "Notice period must be at least 1 month")
      .max(12, "Notice period cannot exceed 12 months")
      .required("Notice period is required"),
    agreementPeriod: Yup.number()
      .min(1, "Agreement period must be at least 1 year")
      .max(10, "Agreement period cannot exceed 10 year")
      .required("Agreement period is required"),
    coolingPeriod: Yup.number()
      .typeError("Cooling period must be a number")
      .integer("Cooling period must be a whole number")
      .min(1, "Cooling period must be at least 1 day")
      .max(90, "Cooling period cannot exceed 90 day")
      .required("Cooling period is required"),
    lockInPeriod: Yup.number()
      .typeError("Lock-in period must be a number")
      .integer("Lock-in period must be a whole number")
      .min(0, "Lock-in period must be at least 0 months")
      .max(60, "Lock-in period cannot exceed 60 months")
      .required("Lock-in period is required"),
    rentCollectionType: Yup.string().required(
      "Rent Collection Type is required",
    ),
  };

  return Yup.object().shape(schema);
};

// Step 2 Validation Schema
const stepTwoSchema = Yup.object().shape({
  agreementChargeType: Yup.string().required(
    "Agreement Charges Type is required",
  ),

  licenseeValue: Yup.number()
    .typeError("Enter valid value")
    .when("agreementChargeType", {
      is: (val) => val === "1",
      then: (schema) =>
        schema
          .required("Licensee amount is required")
          .min(0, "Cannot be negative"),
      // otherwise: (schema) =>
      //   schema.required("Licensee % is required").min(0).max(100),
      otherwise: (schema) =>
        schema
          .required("Licensee % is required")
          .min(0, "Licensee value cannot be negative")
          .max(100, "Licensee value must be less than or equal to 100%"),
    }),

  licensorValue: Yup.number()
    .typeError("Enter valid value")
    .when("agreementChargeType", {
      is: (val) => val === "1",
      then: (schema) =>
        schema
          .required("Licensor amount is required")
          .min(0, "Cannot be negative"),
      otherwise: (schema) =>
        schema.required("Licensor % is required").min(0).max(100),
    })
    .test("sum-100", "Licensee + Licensor must be 100%", function (value) {
      const { agreementChargeType, licenseeValue } = this.parent;

      if (agreementChargeType !== "2") return true;

      const total = Number(licenseeValue || 0) + Number(value || 0);

      return total === 100;
    }),
});

// incrementMonth: Yup.number()
//     .min(1, "Rent increment month cannot be negative")
//     .max(36, "Rent increment month cannot be grater then 3 year")
//     .required("Rent increment is required"),

const EditLOI = ({
  editLoiMutate,
  editLoiLoading,
  handleClose,
  selectedData,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLandlordModalOpen, setIsLandlordModalOpen] = useState(false);
  const [selectedLandlord, setSelectedLandlord] = useState(null);
  let formikRef = useRef(null);
  const stepTwoFormikRef = useRef(null);

  const user = useSelector((state) => state.UserReducer.user);

  const fetchListing = async () =>
    await axios.get(`${Config.apiUrl}/owner/list`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user.token}`,
      },
    });

  const { isLoading, error, data, refetch } = useQuery(
    ["landlordData"],
    fetchListing,
    {
      enabled: currentStep === 1,
    },
  );

  const landlords = data?.data?.list;
  const [height, setHeight] = useState(window.innerHeight);

  // Get flats that don't have lease yet

  const [formData, setFormData] = useState({
    ownerName: "",
    ownerId: "",
    licenseeName: "",
    rentPayable: "",
    securityDeposit: "",
    noticePeriod: "",
    agreementPeriod: "",
    lockInPeriod: "",
    coolingPeriod: "",
    rentCollectionType: "",
    agreementChargeType: "",
    rentIncrementType: "",
    rentIncrementValue: "",
    incrementMonth: "",
  });

  useEffect(() => {
    if (selectedData) {
      setFormData({
        ownerName: selectedData.ownerName ?? "",
        ownerId: selectedData.ownerId ?? "",
        licenseeName: selectedData.licenseeName ?? "",
        rentPayable: selectedData.rent ?? "",
        securityDeposit: selectedData.security ?? "",
        noticePeriod: selectedData.noticePeriod ?? "",
        agreementPeriod: selectedData.agreementPeriod ?? "",
        lockInPeriod: selectedData.lockInPeriod ?? "",
        coolingPeriod: selectedData.coolingPeriod ?? "",
        rentCollectionType:
          selectedData.rentCollectionType != null
            ? String(selectedData.rentCollectionType)
            : "",
        agreementChargeType:
          selectedData.agreementCharges != null
            ? String(selectedData.agreementCharges)
            : "",
        rentIncrementType:
          selectedData.rentIncrementType != null
            ? String(selectedData.rentIncrementType)
            : "",
        rentIncrementValue: selectedData.rentIncrementValue ?? "",
        incrementMonth: selectedData.rentIncrementInterval ?? "",
        tds: selectedData.tds ?? "",
        gst: selectedData.gst ?? "",
        description: selectedData.description ?? "",
        licenseeValue: selectedData.licenseeShare ?? "",
        licensorValue: selectedData.licensorShare ?? "",
      });
    }
  }, [selectedData]);

  useEffect(() => {
    if (selectedData && landlords?.length) {
      const owner = landlords.find((item) => item.id === selectedData.ownerId);

      if (owner) {
        setSelectedLandlord(owner);
      }
    }
  }, [selectedData, landlords]);

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.resetForm();
    }
    const handleResize = () => {
      setHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleStepOneSubmit = (values) => {
    setFormData((prev) => ({
      ...prev,
      ...values,
    }));
    setCurrentStep(2);
  };

  const handleStepTwoSubmit = (values) => {
    const combinedValues = {
      ...formData,
      ...values,
    };

    // Prepare payload with owner data and flats
    const formattedValues = {
      name: combinedValues.ownerName,
      ownerId: combinedValues.ownerId,
      licenseeName: combinedValues.licenseeName,
      rent: Number(combinedValues.rentPayable),
      security: Number(combinedValues.securityDeposit),
      noticePeriod: combinedValues.noticePeriod,
      agreementPeriod: combinedValues.agreementPeriod,
      lockInPeriod: Number(combinedValues.lockInPeriod),
      coolingPeriod: Number(combinedValues.coolingPeriod),
      rentCollectionType: Number(combinedValues.rentCollectionType),
      agreementCharges: Number(combinedValues.agreementChargeType),
      rentIncrementType: combinedValues.rentIncrementType || 0,
      rentIncrementValue: Number(combinedValues.rentIncrementValue) || 0,
      rentIncrementInterval: Number(combinedValues.incrementMonth),
      tds: Number(combinedValues.tds),
      gst: Number(combinedValues.gst),
      description: combinedValues.description,
      licenseeShare: combinedValues.licenseeValue || 0,
      licensorShare: combinedValues.licensorValue || 0,
    };

    editLoiMutate({
      id: selectedData?.id,
      ...formattedValues,
    });
  };

  const handleBackToStepOne = () => {
    setCurrentStep(1);
  };

  // const formatCurrency = (value) => {
  //   if (!value) return "";
  //   return new Intl.NumberFormat("en-IN").format(value);
  // };
  const formatCurrency = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  return new Intl.NumberFormat("en-IN").format(value);
};

  const parseCurrency = (value) => {
    return value.replace(/,/g, "");
  };

  const isValidDecimal = (value, decimals = 2) => {
    const regex = new RegExp(`^\\d*(\\.\\d{0,${decimals}})?$`);
    return regex.test(value);
  };

  // Handle owner selection from modal
  const handleLandlordSelect = (owner) => {
    if (owner) {
      setFormData((prev) => ({
        ...prev,
        ownerName: owner.name || "",
        ownerId: owner.id || "",
        mobile: owner.mobile || "",
      }));
      setSelectedLandlord(owner);
      if (formikRef.current) {
        formikRef.current.setFieldValue("ownerName", owner.name || "");
        formikRef.current.setFieldValue("ownerId", owner.id || "");
        formikRef.current.setFieldValue("mobile", owner.mobile || "");
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        ownerName: "",
        ownerId: "",
        mobile: "",
      }));
      setSelectedLandlord(null);
      if (formikRef.current) {
        formikRef.current.setFieldValue("ownerName", "");
        formikRef.current.setFieldValue("ownerId", "");
        formikRef.current.setFieldValue("mobile", "");
      }
    }
  };

  const StepOneForm = () => {
    const validationSchema = getStepOneSchema();

    return (
      <Formik
        initialValues={formData}
        onSubmit={handleStepOneSubmit}
        validationSchema={validationSchema}
        enableReinitialize
        innerRef={formikRef}
      >
        {({ values, setFieldValue, errors, touched, handleChange }) => {
          return (
            <Form>
              <ModalSubHeading>Step 1</ModalSubHeading>
              <ModalHeading>Basic Details</ModalHeading>
              <ScrollableContent className="max-h-[calc(100dvh-180px)]">
                <Wrapper>
                  {/* Landlord Name */}
                  <InputGroup>
                    <Label htmlFor="ownerName">Owner Name *</Label>
                    {isLoading && (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-gray-600">
                          Loading Owners...
                        </span>
                      </div>
                    )}

                    {error && !isLoading && (
                      <div className="text-red-500 text-sm py-2 px-3 bg-red-50 rounded border border-red-200">
                        Failed to load owners. Please try again.
                        <button
                          onClick={() => refetch()}
                          className="ml-2 text-blue-600 underline"
                        >
                          Retry
                        </button>
                      </div>
                    )}

                    {!isLoading && !error && data && (
                      <CategoryCard
                        title="Landlord"
                        selectedCategory={selectedLandlord}
                        onClick={() => setIsLandlordModalOpen(true)}
                      />
                    )}

                    {!isLoading &&
                      !error &&
                      data &&
                      (!landlords || landlords.length === 0) && (
                        <div className="text-gray-500 text-sm py-2 px-3 bg-gray-50 rounded border border-gray-200">
                          No owner found. Please add a owner first.
                        </div>
                      )}

                    <input
                      type="hidden"
                      name="ownerName"
                      value={values.ownerName}
                      readOnly
                    />
                    <input
                      type="hidden"
                      name="ownerId"
                      value={values.ownerId}
                      readOnly
                    />

                    {errors.ownerName && touched.ownerName && (
                      <ErrorText>{errors.ownerName}</ErrorText>
                    )}
                  </InputGroup>

                  <InputGroup>
                    <Label htmlFor="licenseeName">
                      Licensee Name
                    </Label>
                    <FieldWrappers>
                      <Field
                        type="text"
                        name="licenseeName"
                        id="licenseeName"
                        placeholder="Enter licensee name"
                        value={values.licenseeName}
                        autoComplete="off"
                        onChange={(e) => {
                          setFieldValue("licenseeName", e.target.value);
                        }}
                      />
                    </FieldWrappers>
                  </InputGroup>

                  <CombineInputGroup>
                    <InputGroup>
                      <Label htmlFor="rentPayable">Rent Payable *</Label>
                      <FieldWrappers
                        className={getErrorBorder(
                          touched.rentPayable,
                          errors.rentPayable,
                        )}
                      >
                        <RupeeIcon />
                        <Field
                          type="text"
                          name="rentPayable"
                          id="rentPayable"
                          autoComplete="off"
                          placeholder="Enter rent amount"
                          value={formatCurrency(values.rentPayable)}
                          onChange={(e) => {
                            const rawValue = parseCurrency(e.target.value);
                            if (rawValue === "" || /^\d+$/.test(rawValue)) {
                              setFieldValue("rentPayable", rawValue);
                            }
                          }}
                          className="w-full pl-5"
                        />
                      </FieldWrappers>
                      <div className="text-xs text-gray-500 mt-1">
                        Min: ₹1,000 | Max: ₹20,00,000
                      </div>
                    </InputGroup>

                    <InputGroup>
                      <Label htmlFor="securityDeposit">
                        Security Deposit *
                      </Label>
                      <FieldWrappers
                        className={getErrorBorder(
                          touched.securityDeposit,
                          errors.securityDeposit,
                        )}
                      >
                        <RupeeIcon />
                        <Field
                          type="text"
                          name="securityDeposit"
                          id="securityDeposit"
                          autoComplete="off"
                          placeholder="Enter security amount"
                          value={formatCurrency(values.securityDeposit)}
                          onChange={(e) => {
                            const rawValue = parseCurrency(e.target.value);
                            if (rawValue === "" || /^\d+$/.test(rawValue)) {
                              setFieldValue("securityDeposit", rawValue);
                            }
                          }}
                          className="w-full pl-5"
                        />
                      </FieldWrappers>
                      <div className="text-xs text-gray-500 mt-1">
                        Min: ₹0 | Max: ₹50,00,000
                      </div>
                    </InputGroup>
                  </CombineInputGroup>

                  <CombineInputGroup>
                    <InputGroup>
                      <Label htmlFor="noticePeriod">Notice Period (Months) *</Label>
                      <FieldWrappers
                        className={getErrorBorder(
                          touched.noticePeriod,
                          errors.noticePeriod,
                        )}
                      >
                        <Field
                          type="text"
                          name="noticePeriod"
                          id="noticePeriod"
                          placeholder="Enter notice period"
                          value={formatCurrency(values.noticePeriod)}
                          autoComplete="off"
                          onChange={(e) => {
                            const rawValue = parseCurrency(e.target.value);

                            if (
                              rawValue === "" ||
                              (/^\d+$/.test(rawValue) && Number(rawValue) <= 12)
                            ) {
                              setFieldValue("noticePeriod", rawValue);
                            }
                          }}
                        />
                      </FieldWrappers>
                    </InputGroup>

                    <InputGroup>
                      <Label htmlFor="agreementPeriod">
                        Agreement Period (Years) *
                      </Label>
                      <FieldWrappers
                        className={getErrorBorder(
                          touched.agreementPeriod,
                          errors.agreementPeriod,
                        )}
                      >
                        <Field
                          type="text"
                          name="agreementPeriod"
                          id="agreementPeriod"
                          placeholder="Enter agreement period"
                          value={formatCurrency(values.agreementPeriod)}
                          autoComplete="off"
                          onChange={(e) => {
                            const rawValue = parseCurrency(e.target.value);

                            if (
                              rawValue === "" ||
                              (/^\d+$/.test(rawValue) && Number(rawValue) <= 10)
                            ) {
                              setFieldValue("agreementPeriod", rawValue);
                            }
                          }}
                        />
                      </FieldWrappers>
                    </InputGroup>
                    {/* Notice Period */}
                    {/* <InputGroup>
                      <Label htmlFor="noticePeriod">Notice Period *</Label>
                      <Select
                        sx={{
                          height: 46,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor:
                              touched.noticePeriod && errors.noticePeriod
                                ? "#fb7185"
                                : "#d1d5db",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor:
                              touched.noticePeriod && errors.noticePeriod
                                ? "#fb7185"
                                : "#9ca3af",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#3b82f6",
                          },
                        }}
                        name="noticePeriod"
                        id="noticePeriod"
                        className="w-full px-2"
                        value={values.noticePeriod}
                        onChange={(e) =>
                          setFieldValue("noticePeriod", e.target.value)
                        }
                        //   style={{ height: "46px" }}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          <span className="text-gray-400">
                            Select notice period
                          </span>
                        </MenuItem>
                        {generateMonthOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.noticePeriod && touched.noticePeriod && (
                  <ErrorText>{errors.noticePeriod}</ErrorText>
                )}
                    </InputGroup>

                    <InputGroup>
                      <Label htmlFor="agreementPeriod">
                        Agreement Period *
                      </Label>
                      <Select
                        sx={{
                          height: 46,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor:
                              touched.agreementPeriod && errors.agreementPeriod
                                ? "#fb7185"
                                : "#d1d5db",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor:
                              touched.agreementPeriod && errors.agreementPeriod
                                ? "#fb7185"
                                : "#9ca3af",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#3b82f6",
                          },
                        }}
                        name="agreementPeriod"
                        id="agreementPeriod"
                        className="w-full px-2"
                        value={values.agreementPeriod}
                        onChange={(e) =>
                          setFieldValue("agreementPeriod", e.target.value)
                        }
                        //   style={{ height: "46px" }}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          <span className="text-gray-400">Select period</span>
                        </MenuItem>
                        {agreementPeriodOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.agreementPeriod && touched.agreementPeriod && (
                  <ErrorText>{errors.agreementPeriod}</ErrorText>
                )}
                    </InputGroup> */}
                  </CombineInputGroup>

                  <CombineInputGroup>
                    <InputGroup>
                      <Label htmlFor="coolingPeriod">Cooling Period (Days) *</Label>
                      <FieldWrappers
                        className={getErrorBorder(
                          touched.coolingPeriod,
                          errors.coolingPeriod,
                        )}
                      >
                        <Field
                          type="text"
                          name="coolingPeriod"
                          id="coolingPeriod"
                          placeholder="Enter agreement period"
                          value={formatCurrency(values.coolingPeriod)}
                          autoComplete="off"
                          onChange={(e) => {
                            const rawValue = parseCurrency(e.target.value);

                            if (
                              rawValue === "" ||
                              (/^\d+$/.test(rawValue) && Number(rawValue) <= 90)
                            ) {
                              setFieldValue("coolingPeriod", rawValue);
                            }
                          }}
                        />
                      </FieldWrappers>
                    </InputGroup>

                    <InputGroup>
                      <Label htmlFor="lockInPeriod">Lock In Period (Months) *</Label>
                      <FieldWrappers
                        className={getErrorBorder(
                          touched.lockInPeriod,
                          errors.lockInPeriod,
                        )}
                      >
                        <Field
                          type="text"
                          name="lockInPeriod"
                          id="lockInPeriod"
                          placeholder="Enter lock-in period"
                          value={formatCurrency(values.lockInPeriod)}
                          autoComplete="off"
                          onChange={(e) => {
                            const rawValue = parseCurrency(e.target.value);

                            if (
                              rawValue === "" ||
                              (/^\d+$/.test(rawValue) && Number(rawValue) <= 60)
                            ) {
                              setFieldValue("lockInPeriod", rawValue);
                            }
                          }}
                        />
                      </FieldWrappers>
                    </InputGroup>
                    {/* <InputGroup>
                      <Label htmlFor="coolingPeriod">Cooling Period *</Label>
                      <Select
                        sx={{
                          height: 46,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor:
                              touched.coolingPeriod && errors.coolingPeriod
                                ? "#fb7185"
                                : "#d1d5db",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor:
                              touched.coolingPeriod && errors.coolingPeriod
                                ? "#fb7185"
                                : "#9ca3af",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#3b82f6",
                          },
                        }}
                        name="coolingPeriod"
                        id="coolingPeriod"
                        className="w-full px-2"
                        value={values.coolingPeriod}
                        onChange={(e) =>
                          setFieldValue("coolingPeriod", e.target.value)
                        }
                        // style={{ height: "46px" }}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          <span className="text-gray-400">
                            Select cooling period
                          </span>
                        </MenuItem>
                        {coolingPeriodOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.coolingPeriod && touched.coolingPeriod && (
                    <ErrorText>{errors.coolingPeriod}</ErrorText>
                  )}
                    </InputGroup>

                    <InputGroup>
                      <Label htmlFor="lockInPeriod">Lock In Period *</Label>
                      <Select
                        sx={{
                          height: 46,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor:
                              touched.lockInPeriod && errors.lockInPeriod
                                ? "#fb7185"
                                : "#d1d5db",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor:
                              touched.lockInPeriod && errors.lockInPeriod
                                ? "#fb7185"
                                : "#9ca3af",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#3b82f6",
                          },
                        }}
                        name="lockInPeriod"
                        id="lockInPeriod"
                        className="w-full px-2"
                        value={values.lockInPeriod}
                        onChange={(e) =>
                          setFieldValue("lockInPeriod", e.target.value)
                        }
                        // style={{ height: "46px" }}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          <span className="text-gray-400">
                            Select lock-in period
                          </span>
                        </MenuItem>
                        {lockInPeriodOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.lockInPeriod && touched.lockInPeriod && (
                    <ErrorText>{errors.lockInPeriod}</ErrorText>
                  )}
                    </InputGroup> */}
                  </CombineInputGroup>

                  {/* Rent Collection Type */}
                  <InputGroup>
                    <Label htmlFor="rentCollectionType">
                      Rent Collection Type *
                    </Label>
                    <Select
                      sx={{
                        height: 46,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#d1d5db",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#9ca3af",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#3b82f6",
                        },
                      }}
                      name="rentCollectionType"
                      id="rentCollectionType"
                      className="w-full px-2"
                      value={values.rentCollectionType || ""}
                      onChange={(e) =>
                        setFieldValue("rentCollectionType", e.target.value)
                      }
                      // style={{ height: "46px" }}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        <span className="text-gray-400">
                          Rent collection type
                        </span>
                      </MenuItem>
                      <MenuItem value="1">Advance Rent</MenuItem>
                      <MenuItem value="2">Month-End Rent</MenuItem>
                    </Select>
                    {errors.rentCollectionType &&
                      touched.rentCollectionType && (
                        <ErrorText>{errors.rentCollectionType}</ErrorText>
                      )}
                  </InputGroup>
                </Wrapper>
              </ScrollableContent>
              <ActionSection>
                <SideCancelBtn type="button" onClick={() => handleClose(false)}>
                  Cancel
                </SideCancelBtn>
                <SubmitBtn type="submit">Next</SubmitBtn>
              </ActionSection>

              <SelectItemModal
                isOpen={isLandlordModalOpen}
                onClose={() => setIsLandlordModalOpen(false)}
                onSelect={handleLandlordSelect}
                options={landlords || []}
                selectedItem={selectedLandlord}
                title="Select Landlord"
                searchPlaceholder="Search owner..."
                idKey="id"
                nameKey="name"
                descriptionKeys={["mobile"]}
                emptyMessage="No landlords available"
                noResultsMessage="No landlords found"
              />
            </Form>
          );
        }}
      </Formik>
    );
  };

  const StepTwoForm = () => (
    <Formik
      innerRef={stepTwoFormikRef}
      initialValues={{
        agreementChargeType: formData.agreementChargeType,
        licenseeValue: formData.licenseeValue,
        licensorValue: formData.licensorValue,
        rentIncrementType: formData.rentIncrementType,
        rentIncrementValue: formData.rentIncrementValue,
        incrementMonth: formData.incrementMonth,
        description: formData.description,
        tds: formData.tds,
        gst: formData.gst,
      }}
      // onSubmit={handleStepTwoSubmit}
      onSubmit={(values) => {
        setFormData((prev) => ({
          ...prev,
          ...values,
        }));

        handleStepTwoSubmit(values);
      }}
      validationSchema={stepTwoSchema}
      // enableReinitialize
    >
      {({
        values,
        setFieldValue,
        errors,
        touched,
        setFieldError,
        setFieldTouched,
        validateField,
      }) => (
        <Form>
          <ModalSubHeading>Step 2</ModalSubHeading>
          <ModalHeading>Additional Details</ModalHeading>
          <ScrollableContent className="max-h-[calc(100dvh-180px)]">
            <Wrapper>
              <InputGroup>
                <Label htmlFor="agreementChargeType">
                  Agreement Charges Type *
                </Label>

                <Select
                  sx={{
                    height: 46,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#d1d5db",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#9ca3af",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#3b82f6",
                    },
                  }}
                  name="agreementChargeType"
                  id="agreementChargeType"
                  value={values.agreementChargeType || ""}
                  className="w-full px-2"
                  onChange={(e) => {
                    const value = e.target.value;

                    setFieldValue("agreementChargeType", value);

                    setFieldValue("licenseeValue", "");
                    setFieldValue("licensorValue", "");

                    setFieldError("licenseeValue", undefined);
                    setFieldError("licensorValue", undefined);

                    setFieldTouched("licenseeValue", false);
                    setFieldTouched("licensorValue", false);

                    setTimeout(() => {
                      validateField("agreementChargeType");
                      validateField("licenseeValue");
                      validateField("licensorValue");
                    }, 0);
                  }}
                  displayEmpty
                  style={{ height: "46px" }}
                >
                  <MenuItem value="" disabled>
                    <span className="text-gray-400">
                      Select agreement charges type
                    </span>
                  </MenuItem>
                  <MenuItem value="1">Flat Amount (₹)</MenuItem>
                  <MenuItem value="2">Percentage (%)</MenuItem>
                </Select>
                {errors.agreementChargeType && touched.agreementChargeType && (
                  <ErrorText>{errors.agreementChargeType}</ErrorText>
                )}
              </InputGroup>

              {values.agreementChargeType && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 shadow-sm space-y-4">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-800">
                      Agreement Distribution
                    </p>

                    <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                      {values.agreementChargeType === "1"
                        ? "Flat ₹"
                        : "Percentage %"}
                    </span>
                  </div>

                  {/* Inputs */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Licensee */}
                    <div>
                      <Label className="text-xs block mb-1">
                        Licensee Share
                      </Label>

                      <FieldWrappers className="relative">
                        {values.agreementChargeType === "1" ? (
                          <RupeeIcon />
                        ) : (
                          <PercentIcon />
                        )}
                        <Field
                          type="text"
                          name="licenseeValue"
                          placeholder={
                            values.agreementChargeType === "1"
                              ? "Amount"
                              : "Share"
                          }
                          className="w-full pl-5"
                          autoComplete="off"
                        />
                      </FieldWrappers>
                      {touched.licenseeValue && errors.licenseeValue && (
                        <p className="text-rose-500 text-xs mt-1">
                          {errors.licenseeValue}
                        </p>
                      )}
                    </div>

                    {/* Licensor */}
                    <div>
                      <Label className="text-xs block mb-1">
                        Licensor Share
                      </Label>

                      <FieldWrappers className="relative">
                        {values.agreementChargeType === "1" ? (
                          <RupeeIcon />
                        ) : (
                          <PercentIcon />
                        )}
                        <Field
                          type="text"
                          name="licensorValue"
                          placeholder={
                            values.agreementChargeType === "1"
                              ? "Amount"
                              : "Share"
                          }
                          className="w-full pl-5"
                          autoComplete="off"
                        />
                      </FieldWrappers>
                      {touched.licensorValue && errors.licensorValue && (
                        <p className="text-rose-500 text-xs mt-1">
                          {errors.licensorValue}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Live Total Indicator */}
                  {/* {values.agreementChargeType === "2" && (
                    <div className="mt-3 p-3 rounded-xl bg-gray-50 border text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total</span>
                        <span
                          className={`font-semibold ${
                            Number(values.licenseeValue || 0) +
                              Number(values.licensorValue || 0) ===
                            100
                              ? "text-green-600"
                              : "text-rose-500"
                          }`}
                        >
                          {Number(values.licenseeValue || 0) +
                            Number(values.licensorValue || 0)}
                          %
                        </span>
                      </div>

                      <div className="w-full bg-gray-200 h-2 rounded-full mt-2 overflow-hidden">
                        <div
                          className={`h-full transition-all duration-300 ${
                            Number(values.licenseeValue || 0) +
                              Number(values.licensorValue || 0) ===
                            100
                              ? "bg-green-500"
                              : "bg-rose-400"
                          }`}
                          style={{
                            width: `${Math.min(
                              Number(values.licenseeValue || 0) +
                                Number(values.licensorValue || 0),
                              100,
                            )}%`,
                          }}
                        />
                      </div>
                    </div>
                  )} */}
                </div>
              )}

              {/* Rent Increment */}
              <CombineInputGroup className="gap-4 md:gap-0">
                <InputGroup>
                  <Label htmlFor="rentIncrementType">Rent Increment Type</Label>
                  <Select
                    sx={{
                      height: 46,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#d1d5db",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#9ca3af",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#3b82f6",
                      },
                    }}
                    name="rentIncrementType"
                    id="rentIncrementType"
                    className="w-full px-2"
                    value={values.rentIncrementType || ""}
                    onChange={(e) => {
                      setFieldValue("rentIncrementType", e.target.value);
                      setFieldValue("rentIncrementValue", "");
                    }}
                    style={{ height: "46px" }}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      <span className="text-gray-400">
                        Select increment type
                      </span>
                    </MenuItem>
                    <MenuItem value="1">Flat Amount (₹)</MenuItem>
                    <MenuItem value="2">Percentage (%)</MenuItem>
                  </Select>
                </InputGroup>

                <InputGroup>
                  <Label htmlFor="rentIncrementValue">Increment Value</Label>
                  <FieldWrappers className="w-full">
                    {values.rentIncrementType === "2" ? (
                      <Field
                        type="text"
                        name="rentIncrementValue"
                        id="rentIncrementValue"
                        min="0"
                        max="100"
                        placeholder="Enter % increase"
                        className="w-full pl-2"
                        value={values.rentIncrementValue || ""}
                        autoComplete="off"
                        onChange={(e) => {
                          const rawValue = e.target.value.replace(/,/g, "");

                          if (
                            rawValue === "" ||
                            (isValidDecimal(rawValue, 4) &&
                              Number(rawValue) <= 100)
                          ) {
                            setFieldValue("rentIncrementValue", rawValue);
                          }
                        }}
                      />
                    ) : values.rentIncrementType === "1" ? (
                      <>
                        <RupeeIcon />
                        <Field
                          type="text"
                          name="rentIncrementValue"
                          id="rentIncrementValue"
                          placeholder="Enter amount"
                          value={formatCurrency(values.rentIncrementValue)}
                          onChange={(e) => {
                            const rawValue = parseCurrency(e.target.value);
                            if (rawValue === "" || /^\d+$/.test(rawValue)) {
                              setFieldValue("rentIncrementValue", rawValue);
                            }
                          }}
                          className="w-full pl-5"
                        />
                      </>
                    ) : (
                      <span className="text-gray-400 text-sm pl-2">
                        Select increment type first
                      </span>
                    )}
                  </FieldWrappers>
                  {touched.rentIncrementValue && errors.rentIncrementValue && (
                    <p className="text-rose-600 text-sm">
                      {errors.rentIncrementValue}
                    </p>
                  )}
                </InputGroup>
              </CombineInputGroup>

              <InputGroup>
                <Label htmlFor="incrementMonth">
                  Increment Interval (Months)
                </Label>
                <FieldWrappers>
                  <Field
                    type="text"
                    name="incrementMonth"
                    id="incrementMonth"
                    placeholder="Enter increment month"
                    value={formatCurrency(values.incrementMonth)}
                    autoComplete="off"
                    onChange={(e) => {
                      const rawValue = parseCurrency(e.target.value);
                      if (rawValue === "" || /^\d+$/.test(rawValue)) {
                        setFieldValue("incrementMonth", rawValue);
                      }
                    }}
                  />
                </FieldWrappers>
              </InputGroup>

              <CombineInputGroup>
                <InputGroup>
                  <Label htmlFor="tds">TDS Implications (%)</Label>
                  <FieldWrappers
                    className={
                      touched.tds && errors.tds
                        ? "border-rose-400"
                        : "border-gray-300"
                    }
                  >
                    <Field
                      type="text"
                      inputMode="decimal"
                      name="tds"
                      id="tds"
                      placeholder="Enter TDS implications"
                      value={values.tds || ""}
                      autoComplete="off"
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/,/g, "");

                        if (
                          rawValue === "" ||
                          (isValidDecimal(rawValue, 4) &&
                            Number(rawValue) <= 100)
                        ) {
                          setFieldValue("tds", rawValue);
                        }
                      }}
                    />
                  </FieldWrappers>
                </InputGroup>
                <InputGroup>
                  <Label htmlFor="gst">Gst Implications (%)</Label>
                  <FieldWrappers
                    className={
                      touched.gst && errors.gst
                        ? "border-rose-400"
                        : "border-gray-300"
                    }
                  >
                    <Field
                      type="text"
                      inputMode="decimal"
                      name="gst"
                      id="gst"
                      placeholder="Enter GST implications"
                      value={values.gst || ""}
                      autoComplete="off"
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/,/g, "");

                        if (
                          rawValue === "" ||
                          (isValidDecimal(rawValue, 4) &&
                            Number(rawValue) <= 100)
                        ) {
                          setFieldValue("gst", rawValue);
                        }
                      }}
                    />
                  </FieldWrappers>
                </InputGroup>
              </CombineInputGroup>

              {/* <InputGroup>
                <Label htmlFor="description">Description </Label>
                <Field
                  as={Textarea}
                  id="description"
                  name="description"
                  rows={5}
                  placeholder="Enter Description"
                />
              </InputGroup> */}
            </Wrapper>
          </ScrollableContent>
          <ActionSection>
            <SideCancelBtn type="button" onClick={handleBackToStepOne}>
              Back
            </SideCancelBtn>
            <SubmitBtn
              type="button"
              disabled={editLoiLoading}
              onClick={async () => {
                const formik = stepTwoFormikRef.current;

                if (!formik) return;
                // await formik.setTouched({
                //   rentDate: true,
                //   noticePeriod: true,
                //   agreementPeriod: true,
                //   rentCollectionType: true,
                //   agreementChargeType: true,
                //   incrementMonth: true,
                //   coolingPeriod: true,
                //   lockInPeriod: true,
                //   rentIncrementType: true,
                //   rentIncrementValue: true,
                // });
                await formik.setTouched({
                  agreementChargeType: true,
                  licenseeValue: true,
                  licensorValue: true,
                  rentIncrementType: true,
                  rentIncrementValue: true,
                  incrementMonth: true,
                  tds: true,
                  gst: true,
                  description: true,
                });

                const errors = await formik.validateForm();
                if (Object.keys(errors).length > 0) {
                  return;
                }
                // const currentValues = formik.values;
                // setFormData((prev) => ({
                //   ...prev,
                //   ...currentValues,
                // }));

                // formik.handleSubmit();
                const currentValues = formik.values;

                setFormData((prev) => ({
                  ...prev,
                  ...currentValues,
                }));

                setCurrentStep(3);
              }}
            >
              {/* {editLoiLoading ? "Updating..." : "Update"} */}
              {"Next"}
            </SubmitBtn>
          </ActionSection>
        </Form>
      )}
    </Formik>
  );

  const StepThreeForm = () => (
    <Formik
      initialValues={{
        description: formData.description || "",
      }}
      enableReinitialize
      onSubmit={(values) => {
        handleStepTwoSubmit(values);
      }}
    >
      <Form>
        <ModalSubHeading>Step 3</ModalSubHeading>
        <ModalHeading>Description</ModalHeading>

        <ScrollableContent>
          <Wrapper>
            <InputGroup>
              <Label>Description</Label>

              <Field
                as={Textarea}
                name="description"
                rows={6}
                placeholder="Enter Description"
              />
            </InputGroup>
          </Wrapper>
        </ScrollableContent>

        <ActionSection>
          <SideCancelBtn type="button" onClick={() => setCurrentStep(2)}>
            Back
          </SideCancelBtn>

          <SubmitBtn type="submit">
            {editLoiLoading ? "Updating..." : "Update"}
          </SubmitBtn>
        </ActionSection>
      </Form>
    </Formik>
  );

  return (
    <Container
      style={{
        height: `${height}px`,
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <Header>
        <Title>Edit LOI</Title>
        <button onClick={() => handleClose(false)}>
          <MdClose color="white" size={24} />
        </button>
      </Header>
      <div className="px-6 h-full flex flex-col justify-between">
        {/* {currentStep === 1 ? <StepOneForm /> : <StepTwoForm />} */}
        {currentStep === 1 && <StepOneForm />}
        {currentStep === 2 && <StepTwoForm />}
        {currentStep === 3 && <StepThreeForm />}
      </div>
    </Container>
  );
};

// Styled components
const Header = tw.div`flex items-center justify-between bg-gradient-to-r from-primary-color to-teal-400 py-4 px-4`;
const Container = tw.div`bg-white w-full h-full min-h-[100vh] flex flex-col`;
const Title = tw.h1`text-white text-lg font-medium`;
const ErrorText = tw.div`text-rose-600 text-xs mt-1`;
const Textarea = tw.textarea`min-h-10 max-h-100 border border-gray-300 rounded pl-2 py-2 focus:ring-1 focus:ring-sky-600  focus:border-sky-600 focus:outline-none duration-200 w-full h-96 resize-y break-words overflow-hidden`;

export default EditLOI;
